import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import planoService, { type PlanoTrabalhoHistorico } from '../services/planoTrabalhoService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import { useAuth } from '../context/AuthContext';
import { getMinhasInstituicoesDetalhadas } from '../services/userService';
import { Permission, type InstituicaoDetalhada } from '../types/user.types';
import tipoDocumentoConfigService, { TipoDocumentoConfig } from '../services/tipoDocumentoConfigService';
import DashboardPagination from '../components/DashboardPagination';
import { UI_THEME } from '../ui/theme';
import { FIELD_BASE_CLASS, FIELD_CLASS } from '../components/ui/fieldStyles';
import { buttonClassName } from '../components/ui/buttonStyles';
import Button from '../components/ui/Button';
import { Table, Tbody, Td, Th, Thead, Tr } from '../components/ui/Table';
import { notifyError } from '../utils/notify';
import { toUpperText } from '../utils/formatters';

const STATUS_COLOR: Record<string, string> = {
  APROVADO: 'border-success-line bg-success-soft text-success-text',
  REPROVADO: 'border-danger-line bg-danger-soft text-danger-text',
  ENVIADO: 'border-info-line bg-info-soft text-info-fg',
  RASCUNHO: 'border-line bg-surface-muted text-fg',
  AGUARDANDO_ASSINATURA: 'border-info-line bg-info-soft text-info-fg',
  EM_ANALISE: 'border-info-line bg-info-soft text-info-fg',
  DEVOLVIDO_CORRECAO: 'border-warning-line bg-warning-soft text-warning-text',
  CONCLUIDO: 'border-success-line bg-success-soft text-success-text',
};

const STATUS_LABEL: Record<string, string> = {
  APROVADO: 'Aprovado',
  REPROVADO: 'Reprovado',
  ENVIADO: 'Enviado',
  RASCUNHO: 'Rascunho',
  AGUARDANDO_ASSINATURA: 'Aguardando assinatura',
  EM_ANALISE: 'Em analise',
  DEVOLVIDO_CORRECAO: 'Devolvido para correcao',
  CONCLUIDO: 'Concluido',
};

const MODALIDADE_LABEL: Record<string, string> = {
  TERMO_COLABORACAO: 'Termo de colaboracao',
  TERMO_FOMENTO: 'Termo de fomento',
};

const ROLE_FLOW: Record<string, { title: string; steps: { label: string; detail: string; status?: string }[]; planStatuses?: string[] }> = {
  ORCAMENTO: {
    title: 'Fluxo para Orcamento',
    steps: [
      { label: 'Admissibilidade', detail: 'Analisar a admissibilidade inicial e encaminhar ou devolver quando necessario.', status: 'Em analise de admissibilidade' },
      { label: 'Demanda', detail: 'Encaminhar para analise da secretaria ou devolver por incompatibilidade.', status: 'Em analise de demanda' },
      { label: 'Retornos', detail: 'Tratar devolucoes documentais e reenviar o fluxo quando houver ajuste.', status: 'Devolvido' },
    ],
    planStatuses: ['ENVIADO', 'RASCUNHO', 'REPROVADO'],
  },
  SECRETARIA: {
    title: 'Fluxo para Secretaria',
    steps: [
      { label: 'Demanda', detail: 'Receber os planos apos admissibilidade e avaliar a viabilidade da demanda.', status: 'Em analise de demanda' },
      { label: 'Execucao direta', detail: 'Acompanhar os casos de execucao direta apos aprovacao da demanda.', status: 'Iniciado / Em execucao' },
      { label: 'Pendencias', detail: 'Registrar observacoes e reenviar para ajuste quando necessario.', status: 'Devolvido' },
    ],
    planStatuses: ['ENVIADO', 'RASCUNHO'],
  },
  CONVENIOS: {
    title: 'Fluxo para Convenios',
    steps: [
      { label: 'Minuta do termo de fomento', detail: 'Elaborar a minuta antes do envio para a Procuradoria Geral do Municipio PGM.', status: 'Antes da PGM' },
      { label: 'Formalizacao do termo de fomento', detail: 'Depois do parecer da PGM, formalizar o termo de fomento e preparar a execucao.', status: 'Depois da PGM' },
    ],
    planStatuses: ['ENVIADO', 'APROVADO', 'REPROVADO'],
  },
  JURIDICO: {
    title: 'Fluxo para Juridico',
    steps: [
      { label: 'Parecer', detail: 'Emitir parecer quando o fluxo exigir validacao juridica do instrumento.', status: 'Aguardando parecer' },
      { label: 'Ajustes', detail: 'Registrar exigencias e devolver ao setor responsavel quando houver ressalvas.', status: 'Devolvido' },
    ],
    planStatuses: ['ENVIADO', 'REPROVADO'],
  },
  OPERADOR: {
    title: 'Fluxo para Operador',
    steps: [
      { label: 'Detalhamento', detail: 'Completar formulario e anexos para seguir com a analise.', status: 'Rascunho / Enviado' },
      { label: 'Pendencias', detail: 'Corrigir itens devolvidos pelas areas internas.', status: 'Devolvido' },
      { label: 'Acompanhamento', detail: 'Acompanhar execucao e demais desdobramentos do plano.', status: 'Em execucao / Concluido' },
    ],
    planStatuses: ['RASCUNHO', 'ENVIADO', 'APROVADO', 'REPROVADO'],
  },
};

const formatCurrency = (value?: number | null) =>
  value != null
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value))
    : 'Nao informado';

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString('pt-BR') : 'Sem data';

const formatDateTime = (value?: string) =>
  value ? new Date(value).toLocaleString('pt-BR') : 'Sem data';

const historyLabel = (acao?: string) => {
  switch (acao) {
    case 'CREATE':
      return 'Criacao';
    case 'UPDATE':
      return 'Atualizacao';
    case 'ENVIAR_PARA_APROVACAO':
      return 'Envio para aprovacao';
    case 'APROVAR':
      return 'Aprovacao';
    case 'REPROVAR':
      return 'Reprovacao';
    case 'DEVOLVER_CORRECAO':
      return 'Devolucao para correcao';
    case 'DELETE':
      return 'Exclusao';
    default:
      return acao || 'Atualizacao';
  }
};

const historyTone = (acao?: string) => {
  switch (acao) {
    case 'APROVAR':
      return {
        dot: 'bg-emerald-500',
        badge: 'border-success-line bg-success-soft text-success-text',
      };
    case 'REPROVAR':
      return {
        dot: 'bg-red-500',
        badge: 'border-danger-line bg-danger-soft text-danger-text',
      };
    case 'DEVOLVER_CORRECAO':
      return {
        dot: 'bg-amber-500',
        badge: 'border-warning-line bg-warning-soft text-warning-text',
      };
    case 'ENVIAR_PARA_APROVACAO':
      return {
        dot: 'bg-blue-500',
        badge: 'border-info-line bg-info-soft text-info-fg',
      };
    default:
      return {
        dot: 'bg-slate-400',
        badge: 'border-line bg-surface-muted text-fg',
      };
  }
};

const PlanoTrabalhoPage: React.FC = () => {
  const PAGE_SIZE_OPTIONS = [10, 20, 50];
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, hasPermission } = useAuth();
  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const subtleSurfaceClass = isSaas
    ? 'border-teal-100 bg-teal-50/60 dark:border-teal-900/60 dark:bg-teal-950/30'
    : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const primaryButtonClass = buttonClassName({ variant: 'primary' });
  const secondaryButtonClass = buttonClassName({ variant: 'secondary' });

  const [planos, setPlanos] = useState<PlanoTrabalho[]>([]);
  const [instituicoes, setInstituicoes] = useState<InstituicaoDetalhada[]>([]);
  const [, setDocumentConfigs] = useState<TipoDocumentoConfig[]>([]);
  const [, setLoadingDocsConfig] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [showReprovarModal, setShowReprovarModal] = useState(false);
  const [selectedPlanoId, setSelectedPlanoId] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [timelinePlano, setTimelinePlano] = useState<PlanoTrabalho | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState('');
  const [timelineHistorico, setTimelineHistorico] = useState<PlanoTrabalhoHistorico[]>([]);

  const filterStatus = searchParams.get('status') || '';
  const filterModalidade = searchParams.get('modalidade') || '';
  const filterInstituicao = searchParams.get('instituicao') || '';
  const [query, setQuery] = useState('');
  const [minhaFila, setMinhaFila] = useState(true);
  const hasActiveFilters = Boolean(filterStatus || filterModalidade || filterInstituicao);

  const role = String(user?.role || '').toUpperCase();
  // Gestor da Parceria: atribuicao booleana sobre SECRETARIA, nao role proprio.
  const isGestor = Boolean(user?.gestorParceria);
  // Roles that bypass the institution-link check (internal/admin-level profiles)
  const isInternalRole = isGestor || ['ADMIN', 'CONVENIOS', 'ORCAMENTO', 'JURIDICO', 'JURIDICO_AUXILIAR', 'GESTOR_JURIDICO'].includes(role);
  const isGestorOrAdmin = isInternalRole; // kept for backwards-compat with downstream JSX
  const canCreatePlano = hasPermission(Permission.CREATE_PLANO_TRABALHO);
  // Roles for which there is a meaningful difference between Minha Fila and Todos os Planos
  const showMinhaFilaToggle = isGestor || ['CONVENIOS', 'ORCAMENTO', 'JURIDICO', 'JURIDICO_AUXILIAR', 'GESTOR_JURIDICO'].includes(role);
  const roleFlow = role ? ROLE_FLOW[role] || null : null;
  const recommendedStatuses = roleFlow?.planStatuses ?? ['RASCUNHO', 'ENVIADO', 'APROVADO', 'REPROVADO'];

  const instituicoesUnicas = useMemo(() => {
    const nomes = planos
      .map((p) => p.instituicaoNome)
      .filter((n): n is string => Boolean(n));
    return Array.from(new Set(nomes)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [planos]);

  const planosFiltrados = useMemo(() => {
    const q = query.toLowerCase().trim();
    return planos
      .filter((p) => !filterStatus || (p.status || 'RASCUNHO') === filterStatus)
      .filter((p) => !filterModalidade || p.modalidade === filterModalidade)
      .filter((p) => !filterInstituicao || p.instituicaoNome === filterInstituicao)
      .filter((p) => {
        if (!q) return true;
        const haystack = [
          p.titulo,
          p.instituicaoNome,
          p.emendaCodigo,
          p.numeroProtocolo,
          MODALIDADE_LABEL[p.modalidade || ''] || p.modalidade,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(q);
      });
  }, [planos, filterStatus, filterModalidade, filterInstituicao, query]);

  const planosPaginados = useMemo(() => {
    const start = (page - 1) * pageSize;
    return planosFiltrados.slice(start, start + pageSize);
  }, [page, pageSize, planosFiltrados]);

  const metricas = useMemo(() => ([
    { label: 'Total de planos', value: planos.length, dot: 'bg-slate-400' },
    { label: 'Em analise', value: planos.filter((p) => p.status === 'EM_ANALISE').length, dot: isSaas ? 'bg-teal-500' : 'bg-blue-600' },
    { label: 'Rascunhos', value: planos.filter((p) => (p.status || 'RASCUNHO') === 'RASCUNHO').length, dot: 'bg-amber-500' },
    { label: 'Aprovados ou concluidos', value: planos.filter((p) => ['APROVADO', 'CONCLUIDO'].includes(p.status || '')).length, dot: isSaas ? 'bg-indigo-500' : 'bg-emerald-500' },
  ]), [isSaas, planos]);

  const instituicaoNomePorId = useMemo(
    () => instituicoes.reduce<Record<string, string>>((acc, instituicao) => {
      if (!instituicao.id) return acc;
      acc[instituicao.id] = instituicao.razaoSocial || instituicao.nomeFantasia || instituicao.id;
      return acc;
    }, {}),
    [instituicoes]
  );

  const fetchPlanos = async () => {
    try {
      setLoading(true);
      setLoadError(false);

      const [allPlanos, insts] = await Promise.all([
        planoService.listAll({ minhaFila }),
        getMinhasInstituicoesDetalhadas().catch(() => [] as InstituicaoDetalhada[]),
      ]);

      setInstituicoes(insts);
      setPlanos(allPlanos);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchPlanos();
  }, [user?.role, minhaFila]);

  useEffect(() => {
    let mounted = true;
    const loadDocConfigs = async () => {
      try {
        setLoadingDocsConfig(true);
        const configs = await tipoDocumentoConfigService.listar().catch(() => [] as TipoDocumentoConfig[]);
        if (!mounted) return;
        setDocumentConfigs(Array.isArray(configs) ? configs : []);
      } catch {
        if (mounted) setDocumentConfigs([]);
      } finally {
        if (mounted) setLoadingDocsConfig(false);
      }
    };
    loadDocConfigs();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filterStatus, filterModalidade]);

  const handleNovoPlan = () => {
    const instId = instituicoes.length === 1 ? `?instituicaoId=${instituicoes[0].id}` : '';
    navigate(`/dashboard/novo-plano${instId}`);
  };

  const openTimeline = async (plano: PlanoTrabalho) => {
    try {
      setTimelinePlano(plano);
      setTimelineLoading(true);
      setTimelineError('');
      const historico = await planoService.getHistorico(plano.id);
      const items = Array.isArray(historico)
        ? [...historico].sort((a, b) => new Date(b.dataHora || 0).getTime() - new Date(a.dataHora || 0).getTime())
        : [];
      setTimelineHistorico(items);
    } catch (err: any) {
      setTimelineHistorico([]);
      setTimelineError(err?.response?.data?.error || 'Nao foi possivel carregar a linha do tempo do plano.');
    } finally {
      setTimelineLoading(false);
    }
  };

  const closeTimeline = () => {
    setTimelinePlano(null);
    setTimelineHistorico([]);
    setTimelineError('');
    setTimelineLoading(false);
  };

  return (
    <div className="w-full space-y-6">
      <div className={`rounded-3xl border ${heroClass}`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
              {isSaas ? 'Workspace' : 'Planos'}
            </p>
            <div>
              <h1 className={`text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>
                {isSaas ? 'Esteira de planos de trabalho' : 'Planos de trabalho'}
              </h1>
              <p className={`mt-2 max-w-3xl text-sm leading-6 ${bodyTextClass}`}>
                {isSaas
                  ? 'Acompanhe a fila, encontre protocolos e abra rapidamente o plano completo para agir na etapa atual.'
                  : 'Area geral para criar, consultar e acompanhar planos vinculados as instituicoes e emendas disponiveis para o seu perfil.'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={fetchPlanos}
              className={`${secondaryButtonClass}`}
            >
              Atualizar lista
            </button>
            {canCreatePlano && (
              <button
                type="button"
                onClick={handleNovoPlan}
                className={`${primaryButtonClass}`}
              >
                Novo plano
              </button>
            )}
          </div>
        </div>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4 dark:divide-slate-700">
            {metricas.map((card) => (
              <div key={card.label} className="min-h-[72px] px-4 py-3">
                <p className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${card.dot}`} />
                  {card.label}
                </p>
                <p className={`mt-2 text-2xl font-bold ${strongTextClass}`}>{card.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {roleFlow && (
        <div className={`rounded-3xl border p-5 ${subtleSurfaceClass}`}>
          <div className="space-y-3">
            <div>
              <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${accentTextClass}`}>Contexto do perfil</p>
              <p className={`mt-1 text-lg font-bold ${strongTextClass}`}>{roleFlow.title}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {roleFlow.steps.map((step, idx) => (
                <div key={idx} className={`rounded-2xl border p-4 text-sm ${surfaceClass}`}>
                  <div className={`font-semibold ${accentTextClass}`}>{step.label}</div>
                  <div className={`mt-2 leading-6 ${bodyTextClass}`}>{step.detail}</div>
                  {step.status && <div className={`mt-2 text-xs ${labelTextClass}`}>{step.status}</div>}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className={`font-semibold ${accentTextClass}`}>Atalhos de status:</span>
              <button
                type="button"
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.delete('status');
                  setSearchParams(params);
                  setPage(1);
                }}
                className={`rounded-full border px-3 py-1 ${!filterStatus ? primaryButtonClass : secondaryButtonClass}`}
              >
                Todos
              </button>
              {recommendedStatuses.map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('status', st);
                    setSearchParams(params);
                    setPage(1);
                  }}
                  className={`rounded-full border px-3 py-1 ${filterStatus === st ? primaryButtonClass : secondaryButtonClass}`}
                >
                  {STATUS_LABEL[st] ?? st}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className={`rounded-3xl border p-4 ${surfaceClass}`}>
        <div className="flex flex-wrap items-end gap-3">
          {showMinhaFilaToggle && (
            <div className="flex flex-col gap-1">
              <label className={`text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>Visualizacao</label>
              <div className="flex overflow-hidden rounded-xl border border-slate-300 text-sm font-medium dark:border-slate-600">
                <button
                  type="button"
                  onClick={() => { setMinhaFila(true); setPage(1); }}
                  className={`px-4 py-2 transition-colors ${minhaFila ? primaryButtonClass : 'bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'}`}
                >
                  Minha Fila
                </button>
                <button
                  type="button"
                  onClick={() => { setMinhaFila(false); setPage(1); }}
                  className={`border-l border-slate-300 px-4 py-2 transition-colors dark:border-slate-600 ${!minhaFila ? primaryButtonClass : 'bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'}`}
                >
                  Todos os Planos
                </button>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className={`text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>Buscar</label>
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              placeholder="Título, instituição, protocolo, emenda..."
              className={`${FIELD_BASE_CLASS} w-72`}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={`text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>Status</label>
            <select
              value={filterStatus}
              onChange={(e) => {
                const params = new URLSearchParams(searchParams);
                if (e.target.value) params.set('status', e.target.value); else params.delete('status');
                setSearchParams(params);
                setPage(1);
              }}
              className={FIELD_BASE_CLASS}
            >
              <option value="">Todos</option>
              {Object.entries(STATUS_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className={`text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>Modalidade</label>
            <select
              value={filterModalidade}
              onChange={(e) => {
                const params = new URLSearchParams(searchParams);
                if (e.target.value) params.set('modalidade', e.target.value); else params.delete('modalidade');
                setSearchParams(params);
                setPage(1);
              }}
              className={FIELD_BASE_CLASS}
            >
              <option value="">Todas</option>
              {Object.entries(MODALIDADE_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          {instituicoesUnicas.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-wide text-fg-muted">Instituição</label>
              <select
                value={filterInstituicao}
                onChange={(e) => {
                  const params = new URLSearchParams(searchParams);
                  if (e.target.value) params.set('instituicao', e.target.value); else params.delete('instituicao');
                  setSearchParams(params);
                  setPage(1);
                }}
                className={`${FIELD_BASE_CLASS} max-w-xs`}
              >
                <option value="">Todas</option>
                {instituicoesUnicas.map((nome) => <option key={nome} value={nome}>{nome}</option>)}
              </select>
            </div>
          )}
          {(hasActiveFilters || query) && (
            <button
              type="button"
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.delete('status');
                params.delete('modalidade');
                params.delete('instituicao');
                setSearchParams(params);
                setQuery('');
                setPage(1);
              }}
              className={`${secondaryButtonClass}`}
            >
              Limpar filtros
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className={`rounded-3xl border p-12 text-center ${surfaceClass} ${labelTextClass}`}>
          Carregando planos...
        </div>
      ) : loadError ? (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-10 text-center shadow-sm dark:border-red-800 dark:bg-red-950/30">
          <p className="font-semibold text-danger-text">Erro ao carregar planos</p>
          <p className="mt-2 text-sm text-danger-text">Verifique sua conexao e tente novamente.</p>
          <Button variant="danger" className="mt-4" onClick={fetchPlanos}>
            Tentar novamente
          </Button>
        </div>
      ) : canCreatePlano && !isGestorOrAdmin && instituicoes.length === 0 ? (
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-10 text-center shadow-sm dark:border-amber-800 dark:bg-amber-950/30">
          <p className="font-semibold text-warning-text">Nenhuma instituicao vinculada</p>
          <p className="mt-2 text-sm text-warning-text">
            Para criar planos de trabalho e necessario ter uma instituicao vinculada a sua conta.
          </p>
          <Button variant="warning" className="mt-4" onClick={() => navigate('/dashboard/instituicoes')}>
            Vincular instituicao
          </Button>
        </div>
      ) : planos.length === 0 && !hasActiveFilters && !query && showMinhaFilaToggle && minhaFila ? (
        <div className={`rounded-3xl border p-12 text-center ${surfaceClass}`}>
          <p className={`text-lg font-semibold ${strongTextClass}`}>Sua fila esta vazia</p>
          <p className={`mt-2 text-sm ${labelTextClass}`}>
            Nao ha planos aguardando acao do seu perfil no momento.
          </p>
          <button
            type="button"
            onClick={() => { setMinhaFila(false); setPage(1); }}
            className={`mt-6 ${primaryButtonClass}`}
          >
            Ver todos os planos
          </button>
        </div>
      ) : planos.length === 0 && !hasActiveFilters ? (
        <div className={`rounded-3xl border p-12 text-center ${surfaceClass}`}>
          <p className={`text-lg font-semibold ${strongTextClass}`}>Nenhum plano de trabalho cadastrado</p>
          <p className={`mt-2 text-sm ${labelTextClass}`}>
            Os planos de trabalho sao vinculados as emendas das suas instituicoes.
          </p>
          {canCreatePlano && (
            <button
              type="button"
              onClick={handleNovoPlan}
              className={`mt-6 ${primaryButtonClass}`}
            >
              Criar primeiro plano de trabalho
            </button>
          )}
        </div>
      ) : planosFiltrados.length === 0 ? (
        <div className={`rounded-3xl border p-12 text-center ${surfaceClass}`}>
          <p className={`text-lg font-semibold ${strongTextClass}`}>Nenhum plano encontrado</p>
          <p className={`mt-2 text-sm ${labelTextClass}`}>
            Nenhum plano corresponde aos filtros selecionados. Tente alterar ou limpar os filtros.
          </p>
        </div>
      ) : (
        <div className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
          <Table density="comfortable">
            <Thead>
              <Tr>
                <Th className="w-[30%] text-fg">Plano</Th>
                <Th className="hidden w-[14%] text-fg md:table-cell">Emenda</Th>
                <Th className="hidden w-[14%] text-fg lg:table-cell">Modalidade</Th>
                <Th align="right" className="w-[14%] text-fg">Valor</Th>
                <Th align="center" className="w-[14%] text-fg">Status</Th>
                <Th className="hidden w-[14%] text-fg xl:table-cell">Atualizacao</Th>
                <Th align="right" className="w-[20%] text-fg">Acoes</Th>
              </Tr>
            </Thead>
            <Tbody>
              {planosPaginados.map((plano) => {
                const status = plano.status || 'RASCUNHO';
                const valor = plano.emendaValor ?? plano.valor;
                const instituicaoNome = plano.instituicaoId
                  ? instituicaoNomePorId[plano.instituicaoId] || plano.instituicaoId
                  : 'Nao informada';
                return (
                  <Tr key={plano.id} className="border-b border-slate-100 last:border-b-0 dark:border-slate-800">
                    <Td>
                      <div className="space-y-1">
                        <div className={`break-words font-medium ${strongTextClass}`}>{plano.titulo}</div>
                        <div className={`text-xs ${bodyTextClass}`}>
                          Instituicao: <span className={`font-medium ${strongTextClass}`}>{instituicaoNome}</span>
                        </div>
                        <div className={`text-xs ${labelTextClass}`}>{plano.numeroProtocolo || 'Sem protocolo'}</div>
                        {plano.motivoPendencia && (
                          <div className="text-xs text-warning-text">Pendencia: {plano.motivoPendencia}</div>
                        )}
                      </div>
                    </Td>
                    <Td className="hidden md:table-cell">
                      {plano.emendaCodigo ? (
                        <span className={`inline-block break-all rounded border px-2 py-0.5 font-mono text-xs ${isSaas ? 'border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300' : 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300'}`}>
                          {plano.emendaCodigo}
                        </span>
                      ) : (
                        <span className={`text-xs italic ${labelTextClass}`}>Nao informada</span>
                      )}
                    </Td>
                    <Td className={`hidden lg:table-cell ${bodyTextClass}`}>
                      {MODALIDADE_LABEL[plano.modalidade || ''] || plano.modalidade || 'Nao informada'}
                    </Td>
                    <Td align="right" className={`font-mono text-xs ${bodyTextClass}`}>
                      {formatCurrency(valor)}
                    </Td>
                    <Td align="center">
                      <span className={`inline-flex max-w-full items-center justify-center rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_COLOR[status] ?? STATUS_COLOR.RASCUNHO}`}>
                        {STATUS_LABEL[status] ?? status}
                      </span>
                    </Td>
                    <Td className={`hidden text-xs xl:table-cell ${labelTextClass}`}>
                      {formatDate(plano.dataAtualizacao || plano.aprovadoEm || plano.enviadoEm || plano.dataCriacao)}
                    </Td>
                    <Td align="right">
                      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                        <button
                          type="button"
                          onClick={() => openTimeline(plano)}
                          className={`${buttonClassName({ variant: 'secondary', size: 'sm' })}`}
                        >
                          Timeline
                        </button>
                        <button
                          type="button"
                          onClick={() => plano.id && navigate(`/dashboard/plano/full/${plano.id}`)}
                          disabled={!plano.id}
                          className={`${buttonClassName({ variant: 'secondary', size: 'sm' })}`}
                        >
                          Ver
                        </button>
                        {isGestorOrAdmin && status === 'ENVIADO' && (
                          <>
                            <Button variant="danger-outline" size="sm" onClick={() => { setSelectedPlanoId(plano.id); setMotivo(''); setShowReprovarModal(true); }}>
                              Reprovar
                            </Button>
                          </>
                        )}
                      </div>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
          <DashboardPagination
            page={page}
            pageSize={pageSize}
            totalItems={planosFiltrados.length}
            onPageChange={setPage}
            onPageSizeChange={(value) => {
              setPageSize(value);
              setPage(1);
            }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            itemLabel="planos"
          />
        </div>
      )}

      {timelinePlano && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/40" onClick={closeTimeline} />
          <div className="relative z-10 w-full max-w-4xl rounded-3xl bg-white p-6 shadow-xl dark:bg-slate-900">
            <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-start sm:justify-between dark:border-slate-700">
              <div className="space-y-1">
                <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${accentTextClass}`}>Linha do tempo</p>
                <h2 className={`text-xl font-bold ${strongTextClass}`}>{timelinePlano.titulo}</h2>
                <p className={`text-sm ${labelTextClass}`}>{timelinePlano.numeroProtocolo || timelinePlano.id}</p>
              </div>
              <button
                type="button"
                onClick={closeTimeline}
                className={`${secondaryButtonClass}`}
              >
                Fechar
              </button>
            </div>

            <div className="mt-5 max-h-[70vh] overflow-y-auto pr-1">
              {timelineLoading ? (
                <div className={`rounded-2xl border p-8 text-center text-sm ${subtleSurfaceClass} ${labelTextClass}`}>
                  Carregando linha do tempo...
                </div>
              ) : timelineError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
                  {timelineError}
                </div>
              ) : timelineHistorico.length === 0 ? (
                <div className={`rounded-2xl border p-8 text-center text-sm ${subtleSurfaceClass} ${labelTextClass}`}>
                  Nenhum historico registrado para este plano.
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute bottom-0 left-[11px] top-0 w-px bg-gradient-to-b from-sky-200 via-slate-200 to-slate-100 dark:from-slate-600 dark:via-slate-700 dark:to-slate-800" />
                  <div className="space-y-5">
                    {timelineHistorico.map((item) => {
                      const tone = historyTone(item.acao);
                      const responsavel = item.usuarioNome || item.usuario || 'Sistema';
                      const isInicioEtapa = ['CREATE', 'ENVIAR_PARA_APROVACAO', 'APROVAR'].includes(item.acao);
                      return (
                        <div key={item.id} className="relative pl-9">
                          <div className={`absolute left-0 top-5 h-6 w-6 rounded-full border-4 border-white shadow-lg dark:border-slate-900 ${tone.dot}`} />
                          <div className={`rounded-2xl border p-4 ${surfaceClass}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="space-y-2">
                                <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${tone.badge}`}>
                                  {historyLabel(item.acao)}
                                </span>
                                <p className={`text-sm font-semibold ${strongTextClass}`}>
                                  {item.decisao || item.etapa || 'Atualizacao do workflow'}
                                </p>
                              </div>
                              <p className={`text-xs font-medium ${labelTextClass}`}>{formatDateTime(item.dataHora)}</p>
                            </div>
                            {item.motivo && (
                              <p className={`mt-3 rounded-xl px-3 py-2 text-sm leading-6 ${subtleSurfaceClass} ${bodyTextClass}`}>{item.motivo}</p>
                            )}
                            {item.parecerTexto && (
                              <div className={`mt-3 rounded-xl border px-3 py-2 text-sm ${surfaceClass} ${bodyTextClass}`}>
                                <strong>Parecer:</strong> {item.parecerTexto}
                              </div>
                            )}
                            <div className={`mt-3 text-xs ${labelTextClass}`}>
                              {isInicioEtapa ? `Iniciado por ${responsavel}` : `Ação por ${responsavel}`}
                              {item.documentoTipo ? ` · Documento: ${item.documentoTipo}` : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showReprovarModal && selectedPlanoId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/40" onClick={() => setShowReprovarModal(false)} />
          <div className="relative z-10 w-full max-w-md rounded-3xl bg-white p-6 shadow-xl dark:bg-slate-900">
            <h3 className={`mb-4 text-lg font-semibold ${strongTextClass}`}>Reprovar plano de trabalho</h3>
            <textarea
              className={FIELD_CLASS}
              rows={4}
              value={motivo}
              onChange={(e) => setMotivo(toUpperText(e.target.value))}
              placeholder="Motivo da reprovacao"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button type="button" className={`${secondaryButtonClass}`} onClick={() => setShowReprovarModal(false)}>Cancelar</button>
              <Button variant="danger" disabled={saving} onClick={async () => { if (!motivo.trim()) { notifyError('Informe o motivo'); return; } try { setSaving(true); await planoService.reprovar(selectedPlanoId, motivo); setShowReprovarModal(false); fetchPlanos(); } catch { notifyError('Erro ao reprovar'); } finally { setSaving(false); } }}>
                {saving ? 'Reprovando...' : 'Reprovar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanoTrabalhoPage;
